// Archivo para products
// src/pages/products/Products.js
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CardActions,
  Button,
} from '@mui/material'
import api from '../../services/api'
import useAuth from '../../hooks/useAuth'
import ProductCreateDialog from './ProductCreateDialog'
import { canCreateProduct } from './productPermissions'
import { normalizeArray, pickValue, toSafeText } from '../../utils/defensiveData'

const Products = () => {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const { user } = useAuth()
  const [creating, setCreating] = useState(false)
  const [revision, setRevision] = useState(0)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    api
      .get('/products')
      .then((res) => {
        if (active) setProducts(normalizeArray(res?.data))
      })
      .catch(() => {
        if (active) setError('No se pudieron cargar los productos.')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => { active = false }
  }, [revision, user])

  if (loading) {
    return (
      <Container sx={{ textAlign: 'center', mt: 5 }}>
        <CircularProgress />
      </Container>
    )
  }
  if (error) {
    return (
      <Container sx={{ textAlign: 'center', mt: 5 }}>
        <Typography color="error">{error}</Typography>
        <Button onClick={() => setRevision((value) => value + 1)}>Reintentar</Button>
      </Container>
    )
  }

  return (
    <Container sx={{ mt: 3 }}>
      <Typography variant="h4" gutterBottom>
        Lista de Productos
      </Typography>
      {canCreateProduct(user) && <Button onClick={() => setCreating(true)}>Crear producto</Button>}
      {creating && <ProductCreateDialog onClose={() => setCreating(false)} onCreated={() => { setCreating(false); setRevision((value) => value + 1) }} />}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Abre Ver detalle para consultar la ficha y editar el producto si tu sesión lo permite.
      </Typography>
      <Grid container spacing={3}>
        {products.length > 0 ? (
          products.map((product) => {
            const productId = pickValue(product, 'id', 'id', null)
            const productName = toSafeText(
              pickValue(product, 'name_product', 'nameProduct', pickValue(product, 'name', 'name', '(Producto sin nombre)')),
              '(Producto sin nombre)'
            )
            const productDescription = toSafeText(
              pickValue(product, 'description', 'description', 'Sin descripción operativa'),
              'Sin descripción operativa'
            )

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={String(productId || productName)}>
                <Card sx={{ minHeight: 150 }}>
                  <CardActionArea
                    sx={{ cursor: productId ? 'pointer' : 'not-allowed' }}
                    onClick={() => {
                      if (!productId) return
                      navigate(`/products/${productId}/consumers`, {
                        state: { product },
                      })
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6">{productName}</Typography>
                      <Typography color="textSecondary">{productDescription}</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {`ID: ${toSafeText(productId, 'No disponible')}`}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                  <CardActions>
                    <Button disabled={!productId} onClick={() => navigate(`/products/${productId}`)}>Ver detalle</Button>
                  </CardActions>
                </Card>
              </Grid>
            )
          })
        ) : (
          <Typography>No hay productos registrados</Typography>
        )}
      </Grid>
    </Container>
  )
}

export default Products
